import {Button, Globe01Icon} from '@translated/babele-react'
import clsx from 'clsx'
import {Trans, useTranslation} from 'react-i18next'
import {
  applicationLanguages,
  detectBrowserLanguage,
  makeLangPairPath,
  SupportedLanguage
} from '~/utils/locales'
import {useLocation} from 'react-router'
import {useEffect, useState} from 'react'
import {getLocalizedPath} from '~/utils/routes'
import {useRootLoaderData} from '~/utils/useRootLoaderData'

export const UserLocaleSuggestionDialog = ({
  isLanguageSuggestionDismissed,
  dismissLanguageSuggestion
}: {
  isLanguageSuggestionDismissed: boolean
  dismissLanguageSuggestion: () => void
}) => {
  const [detectedUserLanguage, setDetectedUserLanguage] = useState<SupportedLanguage | null>(null)

  const location = useLocation()
  const {pathData} = useRootLoaderData()
  const currentLanguage = pathData?.language

  const {t, ready} = useTranslation('common', {
    lng: detectedUserLanguage || undefined,
    useSuspense: false
  })

  const isUserLanguageDifferentFromCurrent = !detectedUserLanguage?.startsWith(
    currentLanguage || ''
  )
  const isLanguageSuggestionEnabled =
    isUserLanguageDifferentFromCurrent && !isLanguageSuggestionDismissed
  const languageName = applicationLanguages.find(({code}) => code == detectedUserLanguage)?.label

  const handleUpdateLanguage = () => {
    if (!pathData?.currentPath || !detectedUserLanguage) return

    const localizedPath = getLocalizedPath({
      path: pathData?.currentPath,
      language: detectedUserLanguage
    })

    dismissLanguageSuggestion()
    window.location.href = localizedPath + makeLangPairPath(pathData?.langPair) + location.search
  }

  useEffect(() => {
    setDetectedUserLanguage(detectBrowserLanguage())
  }, [])

  if (!languageName || !pathData?.currentPath || !ready || !isLanguageSuggestionEnabled) return null

  return (
    <div
      id="userLocaleSuggestionDialog"
      role="status"
      className={clsx(
        'flex flex-col gap-4 rounded-md p-4 text-neutral-1000',
        'shadow-md',
        'border-1',
        'border-neutral-100',
        'bg-neutral-25',
        'absolute top-[calc(100%_+_theme(spacing.1))] z-20 animate-fade-in outline-none'
      )}
    >
      <div className="flex items-center gap-2 text-sm">
        <Globe01Icon variant={{size: 'xs'}} />
        <span>
          <Trans
            t={t}
            i18nKey="update_language_popover"
            components={{
              '1': <span className="font-medium" />
            }}
            values={{language: languageName}}
          />
        </span>
      </div>
      <div className="flex items-center gap-1">
        <Button type="button" variant={{tone: 'accent'}} onClick={handleUpdateLanguage}>
          {t('update_language_popover_button', {language: languageName})}
        </Button>
        <Button
          type="button"
          variant={{mode: 'ghost'}}
          className="!text-neutral-1000"
          onClick={dismissLanguageSuggestion}
        >
          {t('update_language_popover_cancel')}
        </Button>
      </div>
    </div>
  )
}
